import { z } from 'zod';
import type {
  CapitalGlobalAssistanceCatalogItem,
  CapitalGlobalCoverageCatalogItem,
  CapitalGlobalSelectOption,
} from './coverages-catalog';
import {
  findBenefitOption,
  isCapitalGlobalBasicCoverage,
  usesBenefitSelection,
  usesBenefitSelectionWithPercent,
} from './coverages-catalog';
import { findCapitalGlobalExclusionConflicts } from './exclusion-group-conflicts.util';
import {
  findCoverageByCode,
  getCapitalBelowMinMessage,
  getCapitalExceedsMaxMessage,
  getCoverageCapitalsByCode,
  getEffectiveLimits,
  getInvalidEffectiveRangeMessage,
  getMaxAllowedPercent,
  getPercentReferenceCapital,
  hasCatalogMin,
  isEffectiveRangeInvalid,
  type CoverageLimitsContext,
} from './coverage-limits.util';
import {
  findCoverageRelationConflicts,
  findDependentCapitalConflicts,
  type CoverageRelationConflict,
} from './coverage-relations.util';
import {
  deriveGroupCoverages,
  type DerivedCoverageRow,
} from '@/features/quotes/components/capital-global/steps/utils/derive-group-coverages.util';
import type { GroupReferenceCapitals } from '@/features/quotes/components/capital-global/steps/utils/map-api.util';
import { formatCurrency } from '@/shared/utils/currencyFormatter';

export const FUNERAL_ASSIST_SELECT_ENABLED = false;

export type StepCoveragesSchemaOptions = {
  groupCapitals?: GroupReferenceCapitals | null;
  /**
   * Catálogos de benefício para a exclusão mútua que ATRAVESSA tipos
   * (`ExclusionGroupCode`). Sem eles, o refino só enxerga cobertura↔cobertura;
   * com eles, bloqueia também benefício↔cobertura no submit. Opcional para não
   * quebrar chamadores que só validam coberturas.
   */
  exclusionCatalogs?: {
    assistanceCatalog: CapitalGlobalAssistanceCatalogItem[];
    assistanceFuneralOptions: CapitalGlobalSelectOption[];
    capitalizationOptions: CapitalGlobalSelectOption[];
  };
};

const coverageRowSchema = z.object({
  enabled: z.boolean(),
  maxIndividualCapital: z.string(),
  percent: z.string(),
  /** Benefício escolhido no seletor (coberturas com `benefitOptions`). */
  benefitCode: z.string().optional(),
});

const assistanceRowSchema = z.object({
  enabled: z.boolean(),
});

export const stepCoveragesBaseSchema = z.object({
  coverages: z.record(z.string(), coverageRowSchema),
  assistances: z.record(z.string(), assistanceRowSchema),
  capitalization: z.string(),
  assistanceFuneral: z.string(),
});

export type StepCoveragesFormValues = z.infer<typeof stepCoveragesBaseSchema>;

function parsePositiveAmount(raw: string): number {
  const n = parseInt(String(raw).replace(/\D/g, ''), 10) / 100;
  if (!Number.isFinite(n) || n <= 0) return 0;
  return n;
}

function parsePercent(raw: string): number {
  const n = parseFloat(String(raw).replace(',', '.'));
  if (!Number.isFinite(n)) return NaN;
  return n;
}

function refineSmallerGroupMinimums(
  coverageCatalog: CapitalGlobalCoverageCatalogItem[],
  morteId: string | undefined,
  groupCapitals: GroupReferenceCapitals,
  data: StepCoveragesFormValues,
  ctx: z.RefinementCtx
): void {
  const derived = deriveGroupCoverages({
    rows: data.coverages,
    morteId,
    groupReferenceCapital: groupCapitals.smallerGroupCapital,
    largestReferenceCapital: groupCapitals.largest,
    coverageCatalog,
  });

  for (const c of coverageCatalog) {
    const row = derived[c.id] as DerivedCoverageRow | undefined;
    if (!row?.enabled || row.capital <= 0) continue;
    if (!hasCatalogMin(c)) continue;

    const min = c.limitMin as number;
    if (row.capital < min) {
      ctx.addIssue({
        code: 'custom',
        message: `Para o grupo ${groupCapitals.smallerGroupLabel}, o valor de "${c.title}" (${formatCurrency(row.capital)}) ficou abaixo do mínimo permitido (${formatCurrency(min)}). Ajuste o percentual.`,
        path: ['coverages', c.id, 'percent'],
      });
    }
  }
}

function refineMorteLockedAtFullCapital(
  morteId: string | undefined,
  referenceCapital: number,
  data: StepCoveragesFormValues,
  ctx: z.RefinementCtx
): void {
  if (!morteId || referenceCapital <= 0) return;

  const morteRow = data.coverages[morteId];
  if (!morteRow?.enabled) {
    ctx.addIssue({
      code: 'custom',
      message: 'A cobertura "Morte" deve estar habilitada.',
      path: ['coverages', morteId, 'enabled'],
    });
    return;
  }

  const morteCapital = parsePositiveAmount(morteRow.maxIndividualCapital);
  if (morteCapital !== referenceCapital) {
    ctx.addIssue({
      code: 'custom',
      message: `A cobertura "Morte" deve corresponder a 100% do capital de referência (${formatCurrency(referenceCapital)}).`,
      path: ['coverages', morteId, 'maxIndividualCapital'],
    });
  }

  const pct = parsePercent(morteRow.percent);
  if (!Number.isFinite(pct) || pct !== 100) {
    ctx.addIssue({
      code: 'custom',
      message: 'A cobertura "Morte" deve estar em 100%.',
      path: ['coverages', morteId, 'percent'],
    });
  }
}

/**
 * Conflitos declarados no produto (`RelationType` + `ParentCoverageCode`).
 * As mensagens vêm de `findCoverageRelationConflicts` — a mesma fonte que a
 * tela usa para acusar em tempo real; aqui elas apenas bloqueiam o submit.
 */
function addCoverageRelationIssues(
  conflicts: CoverageRelationConflict[],
  ctx: z.RefinementCtx
): void {
  for (const conflict of conflicts) {
    ctx.addIssue({
      code: 'custom',
      message: conflict.message,
      path: ['coverages', conflict.coverageId, 'selection'],
    });
  }
}

/**
 * `PROPORTIONAL_TO_COVERAGE` cuja cobertura-base não está contratada (ou está
 * sem capital) neste estipulante.
 *
 * A condição testada é exatamente a que zera a linha no fluxo real:
 * `getPercentReferenceCapital` devolve 0 quando o capital da base declarada não
 * é encontrado, e aí `commitPercentAndCapital` apaga capital e percentual. Sem
 * este refino o corretor recebia "Informe capital máximo individual" para um
 * campo que a tela reapaga a cada tentativa.
 */
function isProportionalBaseMissing(
  item: CapitalGlobalCoverageCatalogItem,
  morteId: string | undefined,
  limitsCtx: CoverageLimitsContext
): boolean {
  if (!item.referenceCoverageCode) return false;
  if (item.id === morteId || isCapitalGlobalBasicCoverage(item)) return false;
  return getPercentReferenceCapital(item, limitsCtx) <= 0;
}

function getProportionalBaseMissingMessage(
  item: CapitalGlobalCoverageCatalogItem,
  coverageCatalog: CapitalGlobalCoverageCatalogItem[]
): string {
  const base = item.referenceCoverageCode
    ? findCoverageByCode(coverageCatalog, item.referenceCoverageCode)
    : undefined;

  if (!base) {
    return `A cobertura "${item.title}" é proporcional a outra cobertura que não está contratada. Revise a seleção deste estipulante.`;
  }

  return `A cobertura "${item.title}" é proporcional a "${base.title}" — contrate "${base.title}" com um capital válido ou desligue "${item.title}".`;
}

/**
 * Exclusão mútua por `ExclusionGroupCode` (ADICIONAL ao `RelationType`), que
 * atravessa tipos — cobertura↔benefício. Mesma fonte da tela; aqui só bloqueia
 * o submit, roteando cada conflito para o path que o front usa. Sem os
 * catálogos de benefício, só valida os grupos entre coberturas.
 */
function refineExclusionGroups(
  coverageCatalog: CapitalGlobalCoverageCatalogItem[],
  data: StepCoveragesFormValues,
  ctx: z.RefinementCtx,
  options?: StepCoveragesSchemaOptions
): void {
  const conflicts = findCapitalGlobalExclusionConflicts(data, {
    coverageCatalog,
    assistanceCatalog: options?.exclusionCatalogs?.assistanceCatalog ?? [],
    assistanceFuneralOptions:
      options?.exclusionCatalogs?.assistanceFuneralOptions ?? [],
    capitalizationOptions:
      options?.exclusionCatalogs?.capitalizationOptions ?? [],
  });

  for (const conflict of conflicts) {
    switch (conflict.kind) {
      case 'coverage':
        ctx.addIssue({
          code: 'custom',
          message: conflict.message,
          path: ['coverages', conflict.coverageId, 'selection'],
        });
        break;
      case 'assistance':
        ctx.addIssue({
          code: 'custom',
          message: conflict.message,
          path: ['assistances', conflict.code, 'enabled'],
        });
        break;
      case 'assistanceFuneral':
        ctx.addIssue({
          code: 'custom',
          message: conflict.message,
          path: ['assistanceFuneral'],
        });
        break;
      case 'capitalization':
        ctx.addIssue({
          code: 'custom',
          message: conflict.message,
          path: ['capitalization'],
        });
        break;
    }
  }
}

function refineCoveragesAgainstCatalog(
  coverageCatalog: CapitalGlobalCoverageCatalogItem[],
  morteId: string | undefined,
  referenceCapital: number,
  morteCapital: number,
  data: StepCoveragesFormValues,
  ctx: z.RefinementCtx,
  options?: StepCoveragesSchemaOptions
): void {
  const limitsCtx: CoverageLimitsContext = {
    referenceCapital,
    morteCapital,
    morteId,
    coverageCapitalsByCode: getCoverageCapitalsByCode(
      coverageCatalog,
      data.coverages
    ),
  };

  const relationConflicts = findCoverageRelationConflicts(coverageCatalog, id =>
    Boolean(data.coverages[id]?.enabled)
  );
  const relationFlagged = new Set(relationConflicts.map(c => c.coverageId));

  for (const c of coverageCatalog) {
    const row = data.coverages[c.id];
    if (!row?.enabled) continue;

    /*
     * Base proporcional ausente ganha uma instrução no lugar dos erros
     * genéricos de valor: o capital desta linha foi zerado justamente por não
     * haver base, então acusar "campo vazio" mandaria o corretor preencher algo
     * que a tela recalcula para vazio. Quando `RelationType` já acusou a mesma
     * linha, a mensagem dele basta — não duplicar.
     */
    if (
      !relationFlagged.has(c.id) &&
      isProportionalBaseMissing(c, morteId, limitsCtx)
    ) {
      ctx.addIssue({
        code: 'custom',
        message: getProportionalBaseMissingMessage(c, coverageCatalog),
        path: ['coverages', c.id, 'selection'],
      });
      continue;
    }

    /*
     * Cobertura contratada pela escolha do benefício: o obrigatório é a
     * OPÇÃO, não o capital — é ela que vai no attach. Nas diárias (WITH_SI,
     * opção sem valor em R$) o % também é obrigatório: é o input do valor da
     * diária (% do capital individual, limitado ao teto) — segue para a
     * validação de percentual abaixo, pulando só os erros de capital (que é
     * derivado do %). Nas demais seleções sem valor em R$ não há mais nada a
     * validar (o InsureMO resolve o SI pelo benefício).
     */
    if (usesBenefitSelection(c)) {
      const option = findBenefitOption(c, row.benefitCode);
      if (!option) {
        ctx.addIssue({
          code: 'custom',
          message: `Selecione uma opção para "${c.title}".`,
          path: ['coverages', c.id, 'maxIndividualCapital'],
        });
      }

      if (usesBenefitSelectionWithPercent(c)) {
        const pct = parsePercent(row.percent);
        const maxPct = getMaxAllowedPercent(c, morteId);
        if (!Number.isFinite(pct) || pct <= 0 || pct > maxPct) {
          const maxLabel = Number.isInteger(maxPct)
            ? String(maxPct)
            : String(maxPct).replace('.', ',');
          ctx.addIssue({
            code: 'custom',
            message: `Informe percentual entre 0 e ${maxLabel} para "${c.title}".`,
            path: ['coverages', c.id, 'percent'],
          });
        }
        continue;
      }

      if (option?.capital == null) continue;
    }

    const limits = getEffectiveLimits(c, limitsCtx);

    const capital = parsePositiveAmount(row.maxIndividualCapital);
    if (capital <= 0) {
      ctx.addIssue({
        code: 'custom',
        // Capital escolhido entre opções de benefício → o campo é um seletor,
        // não um input livre; a instrução precisa refletir isso.
        message: usesBenefitSelection(c)
          ? `Selecione uma opção para "${c.title}".`
          : `Informe capital máximo individual para "${c.title}".`,
        path: ['coverages', c.id, 'maxIndividualCapital'],
      });
    } else if (isEffectiveRangeInvalid(limits)) {
      ctx.addIssue({
        code: 'custom',
        message: getInvalidEffectiveRangeMessage(c),
        path: ['coverages', c.id, 'maxIndividualCapital'],
      });
    } else if (limits.max > 0 && capital > limits.max) {
      ctx.addIssue({
        code: 'custom',
        message: getCapitalExceedsMaxMessage(c, limits.max),
        path: ['coverages', c.id, 'maxIndividualCapital'],
      });
    } else if (hasCatalogMin(c) && capital < limits.min) {
      ctx.addIssue({
        code: 'custom',
        message: getCapitalBelowMinMessage(c, limits.min),
        path: ['coverages', c.id, 'maxIndividualCapital'],
      });
    }

    const pct = parsePercent(row.percent);
    const maxPct = getMaxAllowedPercent(c, morteId);
    if (!Number.isFinite(pct) || pct <= 0 || pct > maxPct) {
      const maxLabel = Number.isInteger(maxPct)
        ? String(maxPct)
        : String(maxPct).replace('.', ',');
      ctx.addIssue({
        code: 'custom',
        message: `Informe percentual entre 0 e ${maxLabel} para "${c.title}".`,
        path: ['coverages', c.id, 'percent'],
      });
    }
  }

  addCoverageRelationIssues(relationConflicts, ctx);
  // Acoplamento de nível dependente↔pai (ex.: Assistência Funeral deve ter o
  // mesmo valor do Auxílio Funeral) — mesma fonte de mensagem da tela.
  addCoverageRelationIssues(
    findDependentCapitalConflicts(
      coverageCatalog,
      id => Boolean(data.coverages[id]?.enabled),
      id => parsePositiveAmount(data.coverages[id]?.maxIndividualCapital ?? '')
    ),
    ctx
  );
  refineExclusionGroups(coverageCatalog, data, ctx, options);

  if (options?.groupCapitals && options.groupCapitals.smallerGroupCapital > 0) {
    refineSmallerGroupMinimums(
      coverageCatalog,
      morteId,
      options.groupCapitals,
      data,
      ctx
    );
  }
}

/**
 * Builds the step 3 validation schema.
 *
 * @param coverageCatalog  Catalog items loaded from API.
 * @param morteId          Catalog id of the "Morte" coverage (identified by name at runtime).
 * @param referenceCapital Individual capital ceiling from step 2 (used as "Morte" upper limit).
 * @param morteCapital     Current "Morte" maxIndividualCapital (used as ceiling for other coverages).
 */
export function createStepCoveragesFormSchema(
  coverageCatalog: CapitalGlobalCoverageCatalogItem[],
  morteId: string | undefined = undefined,
  referenceCapital: number = 0,
  morteCapital: number = 0,
  options?: StepCoveragesSchemaOptions
) {
  return stepCoveragesBaseSchema.superRefine((data, ctx) => {
    refineMorteLockedAtFullCapital(morteId, referenceCapital, data, ctx);
    refineCoveragesAgainstCatalog(
      coverageCatalog,
      morteId,
      referenceCapital,
      morteCapital,
      data,
      ctx,
      options
    );
  });
}
